import { AbilityBuilder, createMongoAbility, type ForcedSubject, type MongoAbility } from '@casl/ability';
import { AREA_BOUND_SUBJECTS, DEFAULT_ROLE_PERMISSIONS, permissionDef, type RoleMatrix } from './permissions';
import type { SessionUser } from './session';

/**
 * CASL abilities. Records carry `areaCode`; technician rules are conditioned on it so the
 * same ability checks single records in API and UI. Permissions per role: `permissions.ts`.
 *
 * - `update`        full technical edit (capacity, splitter, geometry)
 * - `field_update`  field work: status, port links, photos, notes, measurements, small moves
 */
export type Action =
  | 'manage'
  | 'read'
  | 'create'
  | 'update'
  | 'field_update'
  | 'delete'
  | 'import'
  | 'export'
  | 'ping'
  | 'configure'
  | 'ack'
  | 'use';

export type Subject =
  | 'Asset'
  | 'Route'
  | 'Area'
  | 'Monitoring'
  | 'Alert'
  | 'Pricing'
  | 'MasterData'
  | 'User'
  | 'AuditLog'
  | 'Settings'
  | 'Mcp'
  | 'all';

/** A subject type name, or a plain record tagged with `subject('Asset', row)`. */
export type AppAbility = MongoAbility<[Action, Subject | ForcedSubject<Exclude<Subject, 'all'>>]>;

/**
 * Builds the ability from the user's grants and the role → permission matrix (`role_permissions`,
 * editable by admins). Admin always gets everything so nobody can lock the system. Grants tied to
 * an area (technicians) only reach records of that area.
 */
export function defineAbilityFor(
  user: Pick<SessionUser, 'grants'>,
  matrix: RoleMatrix = DEFAULT_ROLE_PERMISSIONS,
): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  for (const grant of user.grants) {
    if (grant.role === 'admin') {
      can('manage', 'all');
      continue;
    }
    for (const code of matrix[grant.role] ?? []) {
      const p = permissionDef(code);
      if (!p) continue;
      if (!grant.area) can(p.action, p.subject);
      // Conditions are declared as variables: CASL types plain-object subjects' conditions loosely.
      else if (p.subject === 'Area') {
        const ownArea = { code: grant.area };
        can(p.action, p.subject, ownArea);
      } else if (AREA_BOUND_SUBJECTS.includes(p.subject)) {
        const inArea = { areaCode: grant.area };
        can(p.action, p.subject, inArea);
      } else can(p.action, p.subject);
    }
  }

  return build();
}

/** Subject helper so plain records can be checked: `ability.can('read', subject('Asset', row))`. */
export { subject } from '@casl/ability';
